import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { ReportDateRange } from '../components/reports/ReportDateRangeFilter';
import { useAuth } from '../contexts/AuthContext';
import { useCompanySettingsQuery } from '../settings/useCompanySettingsQuery';

const buildDefaultRange = (): ReportDateRange => ({
  startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
  endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
});

export const useReportDateRange = () => {
  const { user } = useAuth();
  const [range, setRange] = useState<ReportDateRange>(buildDefaultRange);
  const companyId = user?.company?.id;
  const { data: companySettings } = useCompanySettingsQuery(companyId);

  const minimumStartDate = useMemo(() => {
    const createdAt = companySettings?.company?.createdAt;

    if (!createdAt || !dayjs(createdAt).isValid()) {
      return undefined;
    }

    return dayjs(createdAt).format('YYYY-MM-DD');
  }, [companySettings?.company?.createdAt]);

  useEffect(() => {
    if (!minimumStartDate) {
      return;
    }

    setRange((currentRange) => {
      const nextStartDate = dayjs(currentRange.startDate).isBefore(minimumStartDate)
        ? minimumStartDate
        : currentRange.startDate;
      const nextEndDate = dayjs(currentRange.endDate).isBefore(nextStartDate)
        ? nextStartDate
        : currentRange.endDate;

      if (nextStartDate === currentRange.startDate && nextEndDate === currentRange.endDate) {
        return currentRange;
      }

      return {
        startDate: nextStartDate,
        endDate: nextEndDate,
      };
    });
  }, [minimumStartDate]);

  return {
    range,
    setRange,
    minimumStartDate,
  };
};
